import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import * as commonConstants from '../../../common/constants';
import env from '../../../common/env';
import TokenHelper from '../../../common/TokenHelper';
import utils from '../../../common/utils';
import { buildAiTutorProps } from '../lib/buildAiTutorProps';
import './SalesBotChatLauncher.css';

const ChatComponent = React.lazy(() =>
  import('@vega/aitutor').then((m) => {
    if (!m.ChatComponent) {
      throw new Error('@vega/aitutor: expected named export ChatComponent');
    }
    return { default: m.ChatComponent };
  })
);

function mapBerlinEnvToVegaEnv(berlinEnv) {
  if (berlinEnv === 'stage') return 'stg';
  if (berlinEnv === 'prod') return 'prod';
  if (berlinEnv === 'ppe') return 'ppe';
  return 'qa';
}

/**
 * Same bearer path as other Berlin callers: AI Admin `token/system` via {@link TokenHelper}
 * (see `utils.resolveBerlinDirectAuthHeaders`), then interactive token fallback.
 */
function createAsyncGetAuthToken() {
  return (callback) =>
    new Promise((resolve, reject) => {
      const { isStackB, env: stackBEnv } = utils.getStackBParams();
      const forProd = isStackB && stackBEnv === commonConstants.ENV_PROD;

      TokenHelper.getSys2sysToken(forProd)
        .then((sysToken) => {
          const token = sysToken || utils.getToken();
          if (!token) {
            reject(new Error('No auth token available for Sales Bot'));
            return;
          }
          resolve(callback(token));
        })
        .catch(reject);
    });
}

export default function SalesBotChatLauncher() {
  const [openAIStudy, setOpenAIStudy] = useState(false);
  const [configError] = useState(null);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const vegaEnv = useMemo(() => mapBerlinEnvToVegaEnv(env.BERLIN_ENV), []);

  const { users, agenticGraphConfig, context } = useMemo(() => buildAiTutorProps({ envName: vegaEnv }), [vegaEnv]);

  useEffect(() => {
    const id = window.setTimeout(() => setIsPageLoaded(true), 100);
    return () => window.clearTimeout(id);
  }, []);

  const customConfig = useMemo(
    () => ({
      floatingIcon: {
        toolTipName: 'Compare and explore Pearson products',
        toolTipPlacement: 'left-center'
      },
      cannedMessages: {
        appHeading: 'Sales Bot',
        bookTitlePreText: 'Hello! I can help you explore, compare, ',
        bookTitleSubText: 'and understand Pearson products',
        welcomeBack: ''
      }
    }),
    []
  );

  const getAuthToken = useMemo(() => createAsyncGetAuthToken(), []);

  const toggleAIStudy = useCallback(() => {
    setOpenAIStudy((prev) => !prev);
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <div className="salesBotAiChatWrapper">
          <ChatComponent
            isPageLoaded={isPageLoaded}
            env={vegaEnv}
            users={users}
            context={context}
            agenticGraphConfig={agenticGraphConfig}
            getAuthToken={getAuthToken}
            headers={{ userId: users?.userId }}
            locale="en-us"
            toggleAIStudy={toggleAIStudy}
            openAIStudy={openAIStudy}
            showAIStudyIcon
            showSummary={false}
            showPractice={false}
            showExplain={false}
            enableAgenticAI
            theme="default"
            masterTheme="plus"
            customConfig={customConfig}
            enableMathMLKeyboard
          />
        </div>
      </Suspense>
      {configError ? (
        <div className="salesBotAiChatWrapper__error" role="status">
          {configError}
        </div>
      ) : null}
    </>
  );
}
